import React from 'react';
import { useAnalysisContext } from '../../context/AnalysisContext';
import {
  Info,
  RefreshCw,
  X,
} from 'lucide-react';

/**
 * Step 3: Target Discovery
 *
 * Simple step that allows users to proceed to analysis.
 * No configuration needed - analysis runs instantly when user clicks "Run Analysis".
 */

export const Step3AnalysisConfigurator: React.FC = () => {
  const {
    isRefinementMode,
    exitRefinementMode,
    selectedDisease,
    selectedCellTypes,
  } = useAnalysisContext();

  // No handlers or parameters needed for this step

  return (
    <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-6 py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-2">
          Target Discovery
        </h2>
        <p className="text-[var(--text-secondary)] text-lg">
          Discover disease-specific lncRNA targets for your selected disease and cell types
        </p>
      </div>

      {/* Refinement Mode Notice */}
      {isRefinementMode && (
        <div className="mb-6 origin-card p-4 bg-[var(--origin-blue-500)]/10 border-[var(--origin-blue-500)]">
          <div className="flex items-start gap-3">
            <RefreshCw className="w-5 h-5 text-[var(--origin-blue-500)] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-[var(--text-primary)] mb-1 flex items-center gap-2">
                Refinement Mode
                <span className="px-2 py-0.5 bg-[var(--origin-blue-500)] text-white text-xs rounded-full">
                  Active
                </span>
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-2">
                Adjust analysis parameters to refine your results. Disease and cell types are locked from previous analysis.
              </p>
              <div className="flex items-center gap-4 text-xs text-[var(--text-tertiary)]">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-[var(--text-primary)]">Disease:</span>
                  <span>{selectedDisease}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-[var(--text-primary)]">Cell Types:</span>
                  <span>{selectedCellTypes.disease.length + selectedCellTypes.normal.length} selected</span>
                </div>
              </div>
            </div>
            <button
              onClick={exitRefinementMode}
              className="p-1 hover:bg-[var(--bg-tertiary)] rounded transition-colors"
              title="Exit refinement mode"
            >
              <X className="w-4 h-4 text-[var(--text-secondary)]" />
            </button>
          </div>
        </div>
      )}

      {/* Method Overview */}
      <div className="mb-6 origin-card p-6">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-3">
          How It Works
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mb-4">
          Our target discovery pipeline identifies disease-specific lncRNAs through a multi-step analysis:
        </p>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[var(--origin-blue-500)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-[var(--origin-blue-500)]">1</span>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
                Cell Population Analysis
              </h4>
              <p className="text-sm text-[var(--text-tertiary)]">
                UMAP dimensionality reduction identifies distinct disease and normal cell populations based on expression profiles
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[var(--origin-blue-500)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-[var(--origin-blue-500)]">2</span>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
                Differential Expression
              </h4>
              <p className="text-sm text-[var(--text-tertiary)]">
                Statistical testing (Wilcoxon rank-sum) identifies lncRNAs significantly enriched in disease vs normal cells
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[var(--origin-blue-500)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-[var(--origin-blue-500)]">3</span>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
                Gene Regulatory Networks
              </h4>
              <p className="text-sm text-[var(--text-tertiary)]">
                Correlation analysis reveals lncRNA-gene interactions to identify therapeutic targets with strong regulatory potential
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[var(--origin-blue-500)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-[var(--origin-blue-500)]">4</span>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
                Priority Scoring
              </h4>
              <p className="text-sm text-[var(--text-tertiary)]">
                Targets are ranked by specificity, fold-change, and network connectivity to prioritize the most promising candidates
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Card: Follow-up Actions */}
      <div className="mt-6 origin-card p-5 bg-[var(--bg-secondary)]">
        <h4 className="font-semibold text-[var(--text-primary)] mb-3 text-sm flex items-center gap-2">
          <Info className="w-4 h-4 text-[var(--origin-blue-500)]" />
          After Target Discovery
        </h4>
        <p className="text-sm text-[var(--text-secondary)] mb-3">
          Once you have your target list, you can perform follow-up analyses on specific targets:
        </p>
        <div className="space-y-2 text-sm text-[var(--text-secondary)]">
          <div className="flex items-start gap-2">
            <span className="text-[var(--origin-blue-500)]">→</span>
            <span><strong>Build GRN</strong> for a target to understand its regulatory network</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-[var(--origin-blue-500)]">→</span>
            <span><strong>Simulate Knockdown</strong> to validate therapeutic potential</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-[var(--origin-blue-500)]">→</span>
            <span><strong>Find ASO Sites</strong> for therapeutic design</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3AnalysisConfigurator;
